/**
 * switch-anchor-scroll.js — smooth scroll for in-page anchor links.
 *
 * WHY THIS EXISTS: the theme forces `scroll-behavior: auto !important` globally
 * (assets/switch-custom.css) to kill Horizon's base.css smooth scroll, which
 * caused a "stuck second scroll" bug (a CSS-level smooth scroll animates EVERY
 * programmatic scroll — anchor/focus/scrollIntoView — and blocks the user's own
 * gesture mid-animation). That override makes native `<a href="#id">` clicks snap
 * instantly. This restores SMOOTH scrolling for ANCHOR CLICKS ONLY — a scoped,
 * opt-in animation that does NOT re-enable global CSS smooth, so the bug stays
 * fixed.
 *
 * Uses native `scrollIntoView({behavior:'smooth'})` (not GSAP) on purpose: the
 * "Âncora (SL)" block (blocks/anchor.liquid) sets `scroll-margin-top` to clear
 * the sticky header, and native scroll honors that offset for free — a GSAP
 * tween would have to recompute the header height itself. Logic/integration
 * layer, vanilla JS (FRONTEND-STACK §29). Reduced-motion → instant.
 */
(function () {
  var motionQuery =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Returns the target id if `a` is a same-page hash link, else null. */
  function sameDocHashTarget(a) {
    var href = a.getAttribute('href');
    if (!href) return null;
    if (href.charAt(0) === '#') return href.length > 1 ? href.slice(1) : null;
    // path#hash → only treat as in-page if it resolves to THIS document
    try {
      var url = new URL(a.href, window.location.href);
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search &&
        url.hash.length > 1
      ) {
        return url.hash.slice(1);
      }
    } catch (e) {
      /* malformed href — let the browser handle it */
    }
    return null;
  }

  /* Resolve a hash id to its target element (the anchor block's div, or a named
     anchor). */
  function resolveTarget(id) {
    return (
      document.getElementById(id) ||
      document.querySelector('a[name="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]')
    );
  }

  document.addEventListener(
    'click',
    function (event) {
      // respect modified clicks (new tab/window), non-primary buttons, and
      // anything a component already handled
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      var link = event.target.closest && event.target.closest('a[href]');
      if (!link) return;

      var id = sameDocHashTarget(link);
      if (!id) return;

      var target = resolveTarget(id);
      if (!target) return; // unknown target → leave default behavior alone

      event.preventDefault();

      var reduce = motionQuery ? motionQuery.matches : false;
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });

      // keep the URL hash in sync without triggering a second (instant) jump
      if (window.history && history.replaceState) {
        history.replaceState(null, '', '#' + id);
      }

      // a11y: move focus to the target so keyboard + screen readers follow the
      // jump. The anchor block is an empty div, so make it focusable just for
      // this, then clean up on blur to avoid a lingering tab stop.
      var hadTabindex = target.hasAttribute('tabindex');
      if (!hadTabindex) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      if (!hadTabindex) {
        target.addEventListener(
          'blur',
          function cleanup() {
            target.removeAttribute('tabindex');
            target.removeEventListener('blur', cleanup);
          },
          { once: true }
        );
      }
    },
    false
  );

  /* ---- cross-page landing -------------------------------------------------
     Arriving from a `/#id` link on another page, the browser's native one-shot
     hash scroll usually misfires here: the homepage renders its sections + loads
     images progressively, so when the browser scrolls, the target isn't at its
     final position yet (it lands at/near the top). Re-align to the target as the
     layout settles — a few instant passes plus the `load` event — but STOP the
     moment the visitor scrolls, so we never fight a gesture. */
  function alignToHash() {
    if (!window.location.hash || window.location.hash.length < 2) return;
    var id = window.location.hash.slice(1);
    var stopped = false;
    function stop() {
      stopped = true;
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
    }
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    function pass() {
      if (stopped) return;
      var target = resolveTarget(id);
      if (target) target.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
    [0, 200, 500, 900].forEach(function (t) { setTimeout(pass, t); });
    if (document.readyState !== 'complete') {
      window.addEventListener('load', pass, { once: true });
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', alignToHash);
  } else {
    alignToHash();
  }
})();
